import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Search, AlertTriangle, ShieldCheck, CircleGauge, Copy, BookOpen, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const SEV = {
  basso: { color: "#10B981", icon: ShieldCheck, label: "Basso" },
  medio: { color: "#F59E0B", icon: CircleGauge, label: "Medio" },
  critico: { color: "#EF4444", icon: AlertTriangle, label: "Critico" },
};

const SYSTEMS = {
  P: "Powertrain (motore/trasmissione)",
  B: "Body (carrozzeria)",
  C: "Chassis (telaio/freni)",
  U: "Network (rete di comunicazione)",
};

export default function ObdLookup() {
  const [query, setQuery] = useState("");
  const [codes, setCodes] = useState([]);
  const [open, setOpen] = useState(null);
  const nav = useNavigate();

  const load = async (q) => {
    const { data } = await api.get("/obd/codes", { params: q ? { q } : {} });
    setCodes(data);
  };

  useEffect(() => {
    load("");
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(query), 250);
    return () => clearTimeout(t);
  }, [query]);

  const copy = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Codice ${code} copiato`);
  };

  return (
    <div className="pt-24 pb-20 max-w-5xl mx-auto px-5">
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mb-2">Codici OBD-II</h1>
      <p className="text-zinc-400 mb-8">Cerca un codice errore (es. P0300) e scopri cosa significa e come intervenire.</p>

      <div className="relative mb-8">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
        <input
          data-testid="obd-code-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Cerca codice o descrizione..."
          className="w-full rounded-xl bg-[#111823] border border-white/10 pl-12 pr-4 py-3.5 font-mono text-sm focus:border-[#F97316] focus:outline-none"
        />
      </div>

      {codes.length === 0 ? (
        <p className="text-center text-zinc-500 py-12" data-testid="obd-no-results">Nessun codice trovato.</p>
      ) : (
        <div className="grid gap-3">
          {codes.map((c) => {
            const sev = SEV[c.severity] || SEV.medio;
            const SevIcon = sev.icon;
            const isOpen = open === c.code;
            return (
              <div key={c.code} data-testid={`obd-card-${c.code}`} className="rounded-xl bg-[#111823] border border-white/8 overflow-hidden">
                <button
                  onClick={() => setOpen(isOpen ? null : c.code)}
                  className="w-full flex items-center gap-4 p-4 text-left hover:bg-white/[0.02] transition-colors"
                >
                  <span className="font-mono font-bold text-lg text-[#F97316] shrink-0 w-20">{c.code}</span>
                  <span className="flex-1 text-sm text-zinc-200">{c.title}</span>
                  <span className="hidden sm:flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded" style={{ color: sev.color, backgroundColor: sev.color + "22" }}>
                    <SevIcon className="w-3.5 h-3.5" /> {sev.label}
                  </span>
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 pt-1 border-t border-white/5 space-y-3 rise">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-zinc-500 font-mono">{SYSTEMS[c.system]}</span>
                      <button onClick={() => copy(c.code)} className="flex items-center gap-1 text-xs text-[#06B6D4] hover:underline" data-testid={`obd-copy-${c.code}`}>
                        <Copy className="w-3.5 h-3.5" /> Copia codice
                      </button>
                    </div>
                    <p className="text-sm text-zinc-300">{c.description}</p>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5">Cause comuni</p>
                      <ul className="space-y-1">
                        {c.causes.map((x, i) => (
                          <li key={i} className="text-sm text-zinc-300 flex gap-2">
                            <span className="text-[#F97316]">•</span> {x}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="rounded-lg bg-[#F97316]/10 border border-[#F97316]/30 p-3 text-sm text-orange-200">
                      <span className="font-semibold">Consiglio: </span>{c.advice}
                    </div>
                    {c.related_guide && (
                      <button
                        data-testid={`obd-guide-${c.code}`}
                        onClick={() => nav(`/guide?open=${c.related_guide.id}`)}
                        className="w-full flex items-center justify-between gap-2 rounded-lg bg-[#06B6D4]/10 border border-[#06B6D4]/30 hover:bg-[#06B6D4]/20 p-3 transition-colors group"
                      >
                        <span className="flex items-center gap-2 text-sm text-cyan-200">
                          <BookOpen className="w-4 h-4 text-[#06B6D4]" />
                          Guida: <span className="font-semibold">{c.related_guide.title}</span>
                        </span>
                        <ArrowRight className="w-4 h-4 text-[#06B6D4] group-hover:translate-x-1 transition-transform" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
